import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CheckCircle as HealthyIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Cloud as CloudIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
import axiosWithAuth from '../../utils/axiosWithAuth';

const ServiceHealth = () => {
    const [loading, setLoading] = useState(true);
    const [healthStatus, setHealthStatus] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchServiceData();
    }, []);

    const fetchServiceData = async () => {
        try {
            setLoading(true);
            const healthResponse = await axiosWithAuth.get('/services/health/status');
            setHealthStatus(healthResponse.data);
        } catch (err) {
            setError('Failed to fetch service data: ' + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    const triggerHealthCheck = async () => {
        try {
            setLoading(true);
            await axiosWithAuth.post('/services/health/check');
            setSuccess('Health check completed successfully');
            await fetchServiceData(); // Refresh data
        } catch (err) {
            setError('Health check failed: ' + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    const toggleService = async (serviceName) => {
        // Service configuration is managed via environment variables
        // This is just a placeholder for future implementation
        setSuccess(`${serviceName === 'external_service' ? 'AWC' : 'CAS'} service configuration is managed via environment variables. Changes require application restart.`);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'healthy':
                return <HealthyIcon color="success" />;
            case 'unhealthy':
                return <WarningIcon color="warning" />;
            case 'error':
                return <ErrorIcon color="error" />;
            default:
                return <WarningIcon color="disabled" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy':
                return 'success';
            case 'unhealthy':
                return 'warning';
            case 'error':
                return 'error';
            default:
                return 'default';
        }
    };

    if (loading && Object.keys(healthStatus).length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Header */}
            <Typography variant="h5" gutterBottom>
                Service Health & Control
            </Typography>

            {/* Service Status Cards */}
            <Grid container spacing={3}>
                {/* AWC Service (External) */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <CloudIcon sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    AWC Service
                                </Typography>
                            </Box>

                            {/* Status Icon and Chip */}
                            <Box display="flex" alignItems="center" mb={3}>
                                {getStatusIcon(healthStatus.external_service?.status)}
                                <Chip
                                    label={healthStatus.external_service?.status || 'Unknown'}
                                    color={getStatusColor(healthStatus.external_service?.status)}
                                    sx={{ ml: 1 }}
                                />
                            </Box>

                            {/* Control Buttons */}
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={triggerHealthCheck}
                                    disabled={loading}
                                    size="small"
                                >
                                    Status Check
                                </Button>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={healthStatus.external_service?.enabled || false}
                                            onChange={() => toggleService('external_service')}
                                            disabled={loading}
                                        />
                                    }
                                    label="Enable/Disable"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* CAS Service */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <AnalyticsIcon sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    CAS Service
                                </Typography>
                            </Box>

                            {/* Status Icon and Chip */}
                            <Box display="flex" alignItems="center" mb={3}>
                                {getStatusIcon(healthStatus.cas_service?.status)}
                                <Chip
                                    label={healthStatus.cas_service?.status || 'Unknown'}
                                    color={getStatusColor(healthStatus.cas_service?.status)}
                                    sx={{ ml: 1 }}
                                />
                            </Box>

                            {/* Control Buttons */}
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={triggerHealthCheck}
                                    disabled={loading}
                                    size="small"
                                >
                                    Status Check
                                </Button>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={healthStatus.cas_service?.enabled || false}
                                            onChange={() => toggleService('cas_service')}
                                            disabled={loading}
                                        />
                                    }
                                    label="Enable/Disable"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ServiceHealth;
